import { buildMonthGrid, toDateKey, isSameDay, isSameMonth, isPastDate, isFutureLockedDate, weekdayKeyOf } from './dateUtils';
import { computeEffectiveSlots, statusColors, bookingCountsForDate, withRemainingCapacity } from './useEffectiveSlots';
import { occupancyColor } from '../../lib/heatColor';

const WEEKDAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function MonthView({ currentDate, weeklySlots, slotOverrides, bookings = [], onSelectDate, compact = false, heatView = false }) {
  const weeks = buildMonthGrid(currentDate);
  const today = new Date();

  return (
    <div className="rounded-2xl bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] overflow-hidden">
      <div className="grid grid-cols-7 border-b border-[#e8eaed] dark:border-[#3c4043]">
        {WEEKDAY_HEADERS.map(d => (
          <div key={d} className={compact ? 'py-1 text-center text-[9px] font-semibold text-[#9aa0a6]' : 'py-2 text-center text-[11px] font-semibold uppercase tracking-widest text-[#9aa0a6]'}>
            {compact ? d.slice(0, 1) : d}
          </div>
        ))}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 border-b border-[#e8eaed] dark:border-[#3c4043] last:border-b-0">
          {week.map(date => {
            const dateKey = toDateKey(date);
            const rawEffective = computeEffectiveSlots(weeklySlots, slotOverrides, dateKey, weekdayKeyOf(date));
            const counts = bookingCountsForDate(bookings, dateKey);
            const effective = { ...rawEffective, slots: withRemainingCapacity(rawEffective.slots, counts) };
            const isPast = isPastDate(date, today);
            const isFutureLocked = isFutureLockedDate(date, today);
            const isLocked = isPast || isFutureLocked;
            const colors = statusColors(effective, isLocked);
            const totalBooked = Object.values(counts).reduce((a, b) => a + b, 0);
            const totalCapacity = effective.slots.reduce((a, s) => a + (Number(s.capacity) || 0), 0);
            const occupancyPct = totalCapacity > 0 ? totalBooked / totalCapacity : null;
            const inMonth = isSameMonth(date, currentDate);
            const isToday = isSameDay(date, today);
            const showHeat = heatView && !effective.isClosed && !isLocked && occupancyPct !== null;

            return (
              <button key={dateKey} type="button" disabled={isLocked} onClick={() => onSelectDate(date)}
                className={(compact ? 'min-h-[24px] p-0.5' : 'min-h-[72px] p-2') + ' text-left border-r border-[#e8eaed] dark:border-[#3c4043] last:border-r-0 transition-colors duration-150' + (isLocked ? ' cursor-not-allowed' : (colors ? '' : ' hover:bg-[#f8fafd] dark:hover:bg-[#28292c]'))}
                style={{
                  backgroundColor: showHeat ? occupancyColor(occupancyPct) : (colors ? colors.bg : undefined),
                  opacity: !inMonth ? 0.35 : (isLocked ? 0.6 : undefined),
                  boxShadow: isToday ? 'inset 0 0 0 2px #1a73e8' : undefined,
                }}>
                <div className="flex items-center justify-between">
                  <span
                    className={
                      (compact ? 'text-[9px] font-semibold' : 'text-xs font-semibold') +
                      (isToday ? ' text-[#1a73e8]' : isLocked ? ' text-[#9aa0a6]' : ' text-[#202124] dark:text-[#e8eaed]')
                    }
                  >
                    {date.getDate()}
                  </span>
                  {showHeat && (
                    <span className="text-[9px] font-bold text-[#202124] dark:text-[#e8eaed]">{Math.round(occupancyPct * 100)}%</span>
                  )}
                </div>
                {colors && !compact && !isLocked && !showHeat && (
                  <div className="mt-1">
                    <span className="text-[10px] font-medium text-[#5f6368] dark:text-[#9aa0a6]">
                      {effective.slots.length === 0 ? 'No slots' : totalBooked === 0 ? 'No bookings' : `${totalBooked} booked`}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
